// Registro con pago (URL de campaña): /register/paid?ref=<code>&plan=1|2|3
// Paso 1: datos + tarjeta (la cuenta se crea y se cobra aquí) · Paso 2: negocio · Paso 3: verificación del correo.
// Las cuentas con pasos pendientes vuelven siempre a esta pantalla (ver getPaidSignupStep).

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from '../../components/ui/sonner';
import WhatsAppBubble from '../../components/common/WhatsAppBubble';
import { recurringPaymentsApi } from '../../services/recurringPaymentsApi';
import { getEntryCode } from '../../lib/signupEntry';
import { getPaidSignupStep } from './paidSignupApi';
import { SignupShell } from './components/SignupShell';
import { BLOCKING_SUBSCRIPTION_STATUSES, HOME_PATH, primaryButtonClass } from './constants';
import { PaymentStep } from './steps/PaymentStep';
import { ExistingAccountStep } from './steps/ExistingAccountStep';
import { BusinessStep } from './steps/BusinessStep';
import { VerifyStep } from './steps/VerifyStep';

function PaidSignupFlow(): React.ReactElement {
    const { user } = useAuth();
    const [entryCode] = useState(getEntryCode);
    const [existingEmail, setExistingEmail] = useState<string | null>(null);
    // Existing accounts that log in here stay on the payment step even if nothing is pending.
    const [payingExisting, setPayingExisting] = useState(false);

    const pendingStep = getPaidSignupStep(user);

    if (!user && existingEmail) {
        return (
            <ExistingAccountStep
                email={existingEmail}
                onBack={() => setExistingEmail(null)}
                onVerified={async () => {
                    setExistingEmail(null);
                    try {
                        const { has_subscription, subscription } = await recurringPaymentsApi.getSubscription({ force: true });
                        if (has_subscription && subscription && BLOCKING_SUBSCRIPTION_STATUSES.includes(subscription.status)) {
                            toast.info('Ya tienes un plan activo', { description: 'Te llevamos a Reveca.' });
                            setPayingExisting(false);
                        }
                    } catch {
                        // Without the subscription status, let the user pay; the backend rejects duplicates.
                    }
                }}
            />
        );
    }

    if (user && !pendingStep && !payingExisting) {
        return <Navigate to={HOME_PATH} replace />;
    }

    if (!user && !entryCode) {
        return (
            <SignupShell title="Enlace no válido" subtitle="Este enlace de registro no es válido o expiró">
                <div className="text-center space-y-4">
                    <p className="text-sm text-gray-600">
                        Vuelve a abrir el enlace desde nuestra página para elegir tu plan, o inicia sesión si ya tienes cuenta.
                    </p>
                    <a href="/login" className={primaryButtonClass}>
                        Iniciar sesión
                        <ArrowRight className="h-5 w-5" />
                    </a>
                </div>
            </SignupShell>
        );
    }

    switch (pendingStep ?? 'payment') {
        case 'business':
            return <BusinessStep />;
        case 'verify':
            return <VerifyStep />;
        default:
            return (
                <PaymentStep
                    entryCode={entryCode}
                    onExistingAccount={(email) => {
                        // Set before the OTP login so the "nothing pending" redirect does not fire first.
                        setPayingExisting(true);
                        setExistingEmail(email);
                    }}
                    onPaid={() => setPayingExisting(false)}
                />
            );
    }
}

export function PaidSignupPage(): React.ReactElement {
    const { loading } = useAuth();

    return (
        <>
            {loading ? (
                <div className="min-h-screen bg-gradient-to-br from-[#E8F7F7] via-blue-50 to-indigo-50 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 animate-spin text-[#1CB3B3]" />
                </div>
            ) : (
                <PaidSignupFlow />
            )}
            <Toaster />
            <WhatsAppBubble />
        </>
    );
}

export default PaidSignupPage;
